import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBoxOpen, FaCalendarAlt, FaMapMarkerAlt, FaRupeeSign, FaChevronRight, FaTruck, FaSpinner } from 'react-icons/fa';
import SummaryApi from '../common';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("user_id");

  useEffect(() => {
    if (!customerId) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [customerId, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(SummaryApi.order.getById.url, {
        method: SummaryApi.order.getById.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        // Sort orders by date (newest first)
        const sortedOrders = data.data.sort((a, b) => 
          new Date(b.created_date) - new Date(a.created_date)
        );
        setOrders(sortedOrders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Get Status Badge Color
  const getStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes('delivered')) return 'bg-green-100 text-green-700 border-green-200';
    if (s.includes('cancelled')) return 'bg-red-100 text-red-700 border-red-200';
    if (s.includes('shipped')) return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-yellow-50 text-yellow-700 border-yellow-200'; // Default/Placed
  };

  // --- FIXED IMAGE URL HELPER ---
  const getImageUrl = (imgName) => {
    if (!imgName) return "https://via.placeholder.com/150";
    
    // 1. Clean Domain (Remove trailing slash) -> http://192.168.1.44/sri_danth
    const domain = SummaryApi.backendDomain.replace(/\/$/, "");
    
    // 2. Clean Path (Remove leading slash) -> images/product_product_img1...
    const imgPath = imgName.replace(/^\//, "");
    
    // 3. Construct Full URL with /product/ segment
    const fullUrl = `${domain}/product/${imgPath}`;
    
    // Debugging
    console.log("MyOrder Image URL:", fullUrl);
    
    return fullUrl;
  };

  // Helper: Format Date
  const formatDate = (dateString) => {
    if(!dateString || dateString === "0000-00-00") return "N/A";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-500 mt-2">Track and manage your recent purchases</p>
        </div>

        {/* Empty State */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100">
            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaBoxOpen className="text-4xl text-blue-500 opacity-50" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No orders found</h2>
            <p className="text-gray-500 mb-8">Looks like you haven't placed any orders yet.</p>
            <Link 
              to="/product" 
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Order Cards */}
            {orders.map((order) => (
              <div 
                key={order.order_id} 
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition duration-300"
              >
                {/* Card Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Order Placed</p>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                        <FaCalendarAlt className="text-gray-400" />
                        {formatDate(order.order_date)}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Order ID</p>
                      <p className="text-sm font-medium text-gray-900">#{order.order_code}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Total Amount</p>
                      <p className="text-sm font-bold text-gray-900">₹{order.total_amount}</p>
                    </div>
                  </div>
                  
                  <div className={`px-4 py-1.5 rounded-full text-xs font-bold border uppercase tracking-wider ${getStatusStyle(order.order_status)}`}>
                    {order.order_status}
                  </div>
                </div>

                {/* Order Items */}
                <div className="p-6">
                  {order.order_items && order.order_items.map((item) => (
                    <div key={item.order_items_id} className="flex flex-col sm:flex-row gap-6 mb-6 last:mb-0 border-b last:border-0 pb-6 last:pb-0 border-gray-50">
                      {/* Product Image */}
                      <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-100 p-2">
                        <img 
                          src={getImageUrl(item.product_img)} 
                          alt={item.product_name} 
                          className="w-full h-full object-contain mix-blend-multiply"
                          onError={(e) => { 
                              console.log("Failed to load image:", item.product_img);
                              e.target.src = "https://via.placeholder.com/150?text=No+Image"; 
                          }}
                        />
                      </div>

                      {/* Item Details */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-bold text-gray-900">{item.product_name}</h3>
                          <p className="text-lg font-bold text-gray-900">₹{item.price}</p>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Quantity: {item.quantity}</p>
                        
                        {/* Subtotal for item */}
                        <p className="text-sm font-medium text-gray-700 mt-2">
                          Subtotal: ₹{item.subtotal}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer / Address */}
                <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-100 flex flex-col md:flex-row justify-between md:items-center gap-4">
                  <div className="flex items-start gap-3 max-w-xl">
                    <FaMapMarkerAlt className="text-blue-500 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Delivery Address</p>
                      <p className="text-sm text-gray-500 leading-relaxed">{order.order_address}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase">Payment Mode</p>
                        <p className="text-sm font-bold text-gray-800">{order.payment_mode}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;